import { ThemedText } from '@/components/themed-text';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { formatCurrency } from '@/utils/format-currency';
import React from 'react';
import { View, useWindowDimensions } from 'react-native';
import {
  buildXLabels,
  formatCurrencyAxis,
  formatNumberAxis,
  groupByPeriod,
  type ChartPeriod,
} from './chart/chart-utils';
import { LineAreaChart } from './chart/line-area-chart';

type RevenueChartProps = {
  data?: { date: string; value: number }[];
  isLoading?: boolean;
  period?: ChartPeriod;
  title: string;
  type?: 'currency' | 'number';
  color?: string;
};

export function RevenueChart({
  data = [],
  isLoading = false,
  period = '30d',
  title,
  type = 'currency',
  color = '#FD5001',
}: RevenueChartProps) {
  const { height } = useWindowDimensions();
  const chartHeight = Math.max(200, Math.min(400, height * 0.3));

  const formatValue = (val: number) =>
    type === 'currency' ? formatCurrency(val) : String(val);

  const shell = (children: React.ReactNode) => (
    <Card className="w-full">
      <CardHeader>
        <CardTitle>
          <ThemedText className="font-semibold text-xl">{title}</ThemedText>
        </CardTitle>
        {children}
      </CardHeader>
      <CardContent>
        <RevenueBody
          data={data}
          isLoading={isLoading}
          period={period}
          type={type}
          color={color}
          chartHeight={chartHeight}
        />
      </CardContent>
    </Card>
  );

  const totalValue = data.reduce((sum, d) => sum + d.value, 0);

  return shell(
    <View className="mt-2">
      <ThemedText className="font-bold text-2xl" style={{ color }}>
        {formatValue(totalValue)}
      </ThemedText>
      <ThemedText className="mt-1 text-xs text-foreground/60">
        Período: {period === '7d' ? '7 dias' : period === '30d' ? '30 dias' : '90 dias'}
      </ThemedText>
    </View>,
  );
}

function RevenueBody({
  data,
  isLoading,
  period,
  type,
  color,
  chartHeight,
}: {
  data: { date: string; value: number }[];
  isLoading: boolean;
  period: ChartPeriod;
  type: 'currency' | 'number';
  color: string;
  chartHeight: number;
}) {
  if (isLoading) {
    return (
      <View style={{ height: chartHeight }} className="items-center justify-center">
        <ThemedText className="text-sm text-foreground/60">Carregando dados...</ThemedText>
      </View>
    );
  }

  if (data.length === 0) {
    return (
      <View style={{ height: chartHeight }} className="items-center justify-center">
        <ThemedText className="text-sm text-foreground/60">Nenhum dado disponível</ThemedText>
      </View>
    );
  }

  const grouped = groupByPeriod(data, period, ['value']);
  const xLabels = buildXLabels(grouped.map((d) => d.date));

  return (
    <LineAreaChart
      series={[
        {
          values: grouped.map((d) => d.value),
          color,
          showArea: true,
          showEndDot: true,
        },
      ]}
      xLabels={xLabels}
      height={chartHeight}
      formatY={type === 'number' ? formatNumberAxis : formatCurrencyAxis}
    />
  );
}
